import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { Btn, Badge, Loading, EmptyState, Modal } from '../components/ui';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function SkillBadge({ pct, tier }) {
  const p = pct === null || pct === undefined ? 0 : parseFloat(pct);
  const type = p >= 80 ? 'bd-green' : p >= 60 ? 'bd-amber' : p > 0 ? 'bd-red' : 'bd-gray';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      <span className={`bd ${type}`} style={{ fontSize: 9.5 }}>{p.toFixed(0)}%</span>
      {tier === 'operation' && <span className="bd bd-blue" style={{ fontSize: 9 }}>O</span>}
      {tier === 'fund' && <span className="bd bd-amber" style={{ fontSize: 9 }}>F</span>}
      {tier === 'any' && <span className="bd bd-gray" style={{ fontSize: 9 }}>A</span>}
    </span>
  );
}

// A shared (clubbed) slot's operator only spends half their time on this specific
// operation — count it as 0.5 capacity, not a full 1.0, for output/efficiency math.
const slotWeight = (slot) => (slot.is_clubbed ? 0.5 : 1);

// Actual deployed output/hr for one operation: each staffed slot contributes
// weight x 60/(SAM/(skill%/100)) — independent slots weight=1, shared slots weight=0.5.
// Only zeroes the whole row if NOBODY is assigned at all (op can't function).
// A slot with an assigned person at 0%/low skill still physically works there —
// their contribution is naturally ~0, but it doesn't erase other slots' output.
function opOutputHr(op) {
  const present = op.slots.filter(s => !s.is_vacant);
  if (!present.length) return 0;
  return present.reduce((sum, s) => {
    const sk = parseFloat(s.skill_pct || 0);
    if (!sk) return sum;
    return sum + slotWeight(s) * (60 / (parseFloat(op.sam) / (sk / 100)));
  }, 0);
}

function calcEfficiency(ops) {
  if (!ops.length) return 0;
  const opOutputs = ops.map(op => opOutputHr(op));
  if (opOutputs.some(o => o === 0)) return 0;
  const targetHr = Math.min(...opOutputs);
  const totalSam = ops.reduce((s, op) => s + parseFloat(op.sam), 0);
  // Denominator is the STATIC Line Design operator total (e.g. 8), matching the
  // same reference point LD's own efficiency uses — not a recount of who's
  // actually deployed. A shared operator being "1 person, 2 rows" is already
  // accounted for in the LD's own operators_count math (0.5 + 0.5 = 1 person).
  const deployedCount = ops.reduce((s, op) => s + (parseFloat(op.operators_count) || 0), 0);
  if (!deployedCount) return 0;
  return Math.round((targetHr * totalSam) / (deployedCount * 60) * 10000) / 100;
}

function calcBottleneck(ops) {
  if (!ops.length) return null;
  let minOutput = Infinity;
  let bottleneckName = null;
  for (const op of ops) {
    const outputHr = opOutputHr(op);
    if (outputHr === 0) return op.operation_name; // vacant/no-skill = instant bottleneck
    if (outputHr < minOutput) { minOutput = outputHr; bottleneckName = op.operation_name; }
  }
  return bottleneckName;
}

function DeploymentLanding({ onOpen, onNew }) {
  const [divisions, setDivisions] = useState([]);
  const [factories, setFactories] = useState([]);
  const [allFactories, setAllFactories] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDeps = useCallback(async () => {
    setLoading(true);
    try {
      const [depRes, divRes, facRes] = await Promise.all([
        api.get('/deployment'),
        api.get('/divisions'),
        api.get('/factories'),
      ]);
      setDeployments(depRes.data);
      if (Array.isArray(divRes.data)) setDivisions(divRes.data);
      if (Array.isArray(facRes.data)) { setFactories(facRes.data); setAllFactories(facRes.data); }
    }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchDeps(); }, [fetchDeps]);

  useEffect(() => {
    if (selectedDivision) {
      setFactories(allFactories.filter(f => String(f.division_id) === String(selectedDivision)));
      setSelectedFactory('');
    } else {
      setFactories(allFactories);
    }
  }, [selectedDivision, allFactories]);

  const filteredDeployments = deployments.filter(d => {
    if (selectedDivision && String(d.division_id) !== String(selectedDivision)) return false;
    if (selectedFactory && String(d.factory_id) !== String(selectedFactory)) return false;
    if (selectedShift && d.shift_name !== selectedShift) return false;
    return true;
  });

  // Distinct shift names available, for the Shift filter dropdown
  const shiftOptions = [...new Set(deployments.map(d => d.shift_name).filter(Boolean))].sort();

  const activate = async (id) => {
    try { await api.post(`/deployment/${id}/activate`); fetchDeps(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this line? It will move to Deactivated designs — you can reactivate it later.')) return;
    try { await api.post(`/deployment/${id}/deactivate`); fetchDeps(); }
    catch (e) { alert(e.response?.data?.error || 'Error deactivating'); }
  };

  const deleteDep = async (id) => {
    if (!window.confirm(
      'Permanently delete this deployment?\n\nThis erases ALL history for this line — hourly production, daily balancing records, downtime events, WIP — and cannot be undone or recovered.\n\nIf you just want to stop using this line for now, use Deactivate instead — it can be reactivated later with its history intact.'
    )) return;
    try { await api.delete(`/deployment/${id}`); fetchDeps(); }
    catch (e) { alert(e.response?.data?.error || 'Error deleting deployment'); }
  };

  const active = filteredDeployments.filter(d => d.status === 'Active');
  const pending = filteredDeployments.filter(d => d.status === 'Pending');
  const deactivated = filteredDeployments.filter(d => d.status === 'Deactivated');

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Deployment</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Assign operators to production lines based on skills</div>
        </div>
        <Btn variant="primary" size="sm" onClick={onNew}>+ New deployment</Btn>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          <option value="">All divisions</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={selectedFactory} onChange={e => setSelectedFactory(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          <option value="">All factories</option>
          {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select value={selectedShift} onChange={e => setSelectedShift(e.target.value)}
          style={{ padding: '6px 12px', borderRadius: 6, border: '0.5px solid var(--border)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
          <option value="">All shifts</option>
          {shiftOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
     <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
        <div className="kpi blue"><div className="kpi-label" style={{ color: 'var(--acc-t)' }}>Active lines</div><div className="kpi-val" style={{ color: 'var(--acc)' }}>{active.length}</div></div>
        <div className="kpi amber"><div className="kpi-label" style={{ color: 'var(--amb)' }}>Pending activation</div><div className="kpi-val" style={{ color: 'var(--amb)' }}>{pending.length}</div></div>
        <div className="kpi"><div className="kpi-label" style={{ color: 'var(--text-secondary)' }}>Deactivated</div><div className="kpi-val" style={{ color: 'var(--text-secondary)' }}>{deactivated.length}</div></div>
        <div className="kpi"><div className="kpi-label">Total deployments</div><div className="kpi-val">{deployments.length}</div></div>
      </div>

      {pending.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--amb)', marginBottom: 8 }}>Pending activation</div>
          {pending.map(d => (
            <div key={d.id} style={{ background: 'var(--amb-bg)', border: '0.5px solid var(--amb)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Line {d.line_number} · {d.style_number}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{d.manpower} operators · {fmtDate(d.deploy_date)}</div>
              </div>
              <Btn size="sm" onClick={() => onOpen(d)}>Open</Btn>
              <Btn size="sm" variant="primary" onClick={() => activate(d.id)}>Activate</Btn>
              <Btn size="sm" onClick={() => deleteDep(d.id)} style={{ color: 'var(--red)', borderColor: 'var(--red)', background: 'var(--red-bg)' }}>Delete</Btn>
            </div>
          ))}
        </>
      )}

      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8, marginTop: pending.length ? 12 : 0 }}>Active lines</div>
      {loading ? <Loading /> : active.length === 0
        ? <EmptyState message="No active deployments." action={<Btn variant="primary" onClick={onNew}>+ New deployment</Btn>} />
        : active.map(d => (
          <div key={d.id}
            style={{ background: 'var(--bg-primary)', border: '0.5px solid var(--border-md)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', transition: 'border-color 0.15s' }}
            onClick={() => onOpen(d)}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--acc)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-md)'}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Line {d.line_number} · {d.style_number}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{d.manpower} operators · {fmtDate(d.deploy_date)} · {d.shift_name}</div>
            </div>
            <Badge type="green">Active</Badge>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--acc)' }}>{parseFloat(d.plan_efficiency || 0).toFixed(1)}%</span>
            <Btn size="sm">Open →</Btn>
          </div>
        ))
      }

      {deactivated.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8, marginTop: 16 }}>Deactivated designs</div>
          {deactivated.map(d => (
            <div key={d.id} style={{ background: 'var(--bg-secondary)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, opacity: 0.85 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Line {d.line_number} · {d.style_number}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{d.manpower} operators · {fmtDate(d.deploy_date)} · {d.shift_name}</div>
              </div>
              <Badge type="gray">Deactivated</Badge>
              <Btn size="sm" onClick={() => onOpen(d)}>Open</Btn>
              <Btn size="sm" variant="primary" onClick={() => activate(d.id)}>Reactivate</Btn>
              <Btn size="sm" onClick={() => deleteDep(d.id)} style={{ color: 'var(--red)', borderColor: 'var(--red)', background: 'var(--red-bg)' }}>Delete</Btn>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function NewDeploymentModal({ open, onClose, onCreated }) {
  const [lineDesigns, setLineDesigns] = useState([]);
  const [shiftLines, setShiftLines] = useState([]);
  const [selectedLd, setSelectedLd] = useState('');
  const [selectedLine, setSelectedLine] = useState('');
  const [deployDate, setDeployDate] = useState(new Date().toISOString().split('T')[0]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get('/line-design'), api.get('/shift-lines')]).then(([ld, sl]) => {
      setLineDesigns(ld.data); setShiftLines(sl.data);
    });
  }, [open]);

  const handleCreate = async () => {
    if (!selectedLd || !selectedLine) { setError('Select a line design and shift-line'); return; }
    setCreating(true); setError('');
    try {
      await api.post('/deployment', { line_design_id: selectedLd, shift_line_id: selectedLine, deploy_date: deployDate });
      onCreated();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setCreating(false);
  };

  const inp = { padding: '5px 9px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 7, background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit', width: '100%' };

  return (
    <Modal open={open} onClose={onClose} title="New deployment" width={420}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Line design</div>
          <select value={selectedLd} onChange={e => setSelectedLd(e.target.value)} style={inp}>
            <option value="">— select line design —</option>
            {lineDesigns.map(ld => <option key={ld.id} value={ld.id}>{ld.ob_number} · {ld.style_number} · {ld.manpower} operators · {parseFloat(ld.efficiency || 0).toFixed(1)}%</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Shift-line</div>
          <select value={selectedLine} onChange={e => setSelectedLine(e.target.value)} style={inp}>
            <option value="">— select line —</option>
            {shiftLines.map(l => <option key={l.id} value={l.id}>Line {l.line_number} · {l.shift_name} · {l.factory_name}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Deployment date</div>
          <input type="date" value={deployDate} onChange={e => setDeployDate(e.target.value)} style={inp} />
        </div>
        {error && <div style={{ fontSize: 12, color: 'var(--red)', background: 'var(--red-bg)', padding: '7px 10px', borderRadius: 6 }}>{error}</div>}
        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: 7 }}>
          Auto-assignment runs on skill profiles. Any vacant slot = 0% efficiency.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={handleCreate} disabled={creating}>{creating ? 'Creating…' : 'Create deployment'}</Btn>
        </div>
      </div>
    </Modal>
  );
}

function AssignModal({ open, onClose, slot, deploymentId, onAssigned }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !slot) return;
    setLoading(true);
    api.get(`/deployment/${deploymentId}/candidates`, { params: { line_design_item_id: slot.line_design_item_id, slot_id: slot.id } })
      .then(({ data }) => setCandidates(data))
      .catch(() => setCandidates([]))
      .finally(() => setLoading(false));
  }, [open, slot, deploymentId]);

  const handleAssign = async (empId, tier, skillPct) => {
    try {
      await api.put(`/deployment/${deploymentId}/slots/${slot.id}`, { employee_id: empId, match_tier: tier, skill_pct: skillPct });
      onAssigned();
    } catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleRemove = async () => {
    try { await api.put(`/deployment/${deploymentId}/slots/${slot.id}`, { employee_id: null }); onAssigned(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const tierLabel = { direct: 'Direct match', operation: 'Operation match', fund: 'Fund. match', any: 'Any available' };
  const tierColor = { direct: 'green', operation: 'blue', fund: 'amber', any: 'gray' };

  return (
    <Modal open={open} onClose={onClose} title={`Assign — ${slot?.is_clubbed && slot?.clubbed_operation_name && slot.clubbed_operation_name !== slot.operation_name ? `${slot.operation_name} + ${slot.clubbed_operation_name}` : slot?.operation_name}`} width={440}>
      {loading ? <Loading /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {slot?.employee_id && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <Btn size="sm" onClick={handleRemove} style={{ color: 'var(--red)' }}>Remove assignment</Btn>
            </div>
          )}
          {candidates.length === 0
            ? <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>No available operators</div>
            : candidates.map((c, i) => (
              <div key={c.employee_id}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: i === 0 ? 'var(--acc-bg)' : 'var(--bg-primary)', cursor: 'pointer' }}
                onClick={() => handleAssign(c.employee_id, c.tier, c.skill_pct)}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--acc)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>
                    {i === 0 && <span style={{ fontSize: 9.5, background: 'var(--acc)', color: '#fff', padding: '1px 5px', borderRadius: 4, marginRight: 6 }}>Best match</span>}
                    {c.emp_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Line {c.line_number} · {c.emp_id}</div>
                </div>
                <Badge type={tierColor[c.tier]}>{tierLabel[c.tier]}</Badge>
                <SkillBadge pct={c.skill_pct} />
              </div>
            ))
          }
        </div>
      )}
    </Modal>
  );
}

function PoolPanel({ deploymentId, slots, onReleaseElsewhere }) {
  const [pool, setPool] = useState({ absent: [], direct: [], fund: [], unmatched: [], global: [] });
  const [loading, setLoading] = useState(true);
  const [searchCode, setSearchCode] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const fetchPool = useCallback(() => {
    setLoading(true);
    api.get(`/deployment/${deploymentId}/pool`)
      .then(({ data }) => setPool(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [deploymentId]);

  useEffect(() => { fetchPool(); }, [fetchPool, slots]);

  const handleSearch = async () => {
    if (!searchCode.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/deployment/${deploymentId}/search-employee`, { params: { code: searchCode.trim() } });
      setSearchResults(data.results || []);
    } catch (e) { setSearchResults([]); }
    setSearching(false);
  };

  const handleRelease = async (result) => {
    if (!window.confirm(`Release ${result.name} from Line ${result.assignment.line_number} · ${result.assignment.operation_name}?`)) return;
    setReleasing(result.id);
    try {
      await api.put(`/deployment/${result.assignment.deployment_id}/slots/${result.assignment.slot_id}`, { employee_id: null });
      await handleSearch();
      fetchPool();
      onReleaseElsewhere && onReleaseElsewhere();
    } catch (e) { alert(e.response?.data?.error || 'Error releasing'); }
    setReleasing(null);
  };

  const Section = ({ title, emps, chipStyle }) => emps?.length > 0 ? (
    <>
      <div style={{ fontSize: 9.5, fontWeight: 500, color: 'var(--text-tertiary)', letterSpacing: '0.6px', textTransform: 'uppercase', padding: '5px 2px 3px' }}>{title} ({emps.length})</div>
      {emps.map(e => (
        <div key={e.employee_id} style={{ border: '0.5px solid var(--border)', borderRadius: 7, padding: '5px 8px', marginBottom: 3, ...chipStyle }}>
          <div style={{ fontSize: 11, fontWeight: 500 }}>{e.name}</div>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 1 }}>{e.skills?.map(s => s.operation_name).slice(0, 2).join(', ') || 'No skills'}</div>
        </div>
      ))}
    </>
  ) : null;

  return (
    <div style={{ width: 190, minWidth: 190, border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
      <div style={{ padding: '8px 10px', background: 'var(--bg-secondary)', borderBottom: '0.5px solid var(--border)', fontSize: 11.5, fontWeight: 500, color: 'var(--text-secondary)' }}>Operator pool</div>

      {/* Employee code search — find anyone unit-wide and release them from wherever they are */}
      <div style={{ padding: '8px 8px 6px', borderBottom: '0.5px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          <input
            className="inp" placeholder="Employee code…" value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            style={{ flex: 1, fontSize: 11, padding: '4px 6px' }}
          />
          <button onClick={handleSearch} className="btn btn-s" style={{ padding: '4px 8px' }}>Go</button>
        </div>
        {searching && <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>Searching…</div>}
        {searchResults && !searching && searchResults.length === 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>No match found</div>
        )}
        {searchResults && searchResults.map(r => (
          <div key={r.id} style={{ border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', marginTop: 5, background: r.assignment ? 'var(--acc-bg)' : 'var(--grn-bg)' }}>
            <div style={{ fontSize: 11, fontWeight: 600 }}>{r.name}</div>
            <div style={{ fontSize: 9.5, color: 'var(--text-secondary)' }}>{r.emp_code} · Home: Line {r.home_line || '—'}</div>
            {r.assignment ? (
              <>
                <div style={{ fontSize: 10, marginTop: 3 }}>
                  On <strong>Line {r.assignment.line_number}</strong> · {r.assignment.operation_name}
                  {r.assignment.is_absent && <span style={{ color: 'var(--red)' }}> (marked absent)</span>}
                </div>
                <button onClick={() => handleRelease(r)} disabled={releasing === r.id}
                  className="btn btn-s" style={{ marginTop: 4, width: '100%', color: 'var(--red)', borderColor: 'var(--red-bg)', background: 'var(--red-bg)' }}>
                  {releasing === r.id ? 'Releasing…' : 'Release from there'}
                </button>
              </>
            ) : (
              <div style={{ fontSize: 10, color: 'var(--grn)', marginTop: 3 }}>Not currently assigned — available</div>
            )}
          </div>
        ))}
      </div>

      <div style={{ padding: 8, overflowY: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
        {loading ? <div style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: 8 }}>Loading…</div> : (
          <>
            <Section title="Absent" emps={pool.absent} chipStyle={{ background: 'var(--red-bg)', borderColor: '#F09595' }} />
            <Section title="Direct match" emps={pool.direct} chipStyle={{ background: '#F5FBF0', borderColor: '#97C459' }} />
            <Section title="Fund. match" emps={pool.fund} chipStyle={{ background: '#FFFBF3', borderColor: '#FAC775' }} />
            <Section title="Unmatched" emps={pool.unmatched} chipStyle={{}} />
            {pool.global?.length > 0 && (
              <>
                <div style={{ height: '0.5px', background: 'var(--border)', margin: '6px 0' }} />
                <div style={{ fontSize: 9.5, fontWeight: 500, color: 'var(--acc-t)', background: 'var(--acc-bg)', padding: '3px 6px', borderRadius: 4, textAlign: 'center', marginBottom: 4 }}>Global pool</div>
                <Section title="Available" emps={pool.global} chipStyle={{}} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DeploymentDetail({ deployment, onBack, onRefresh }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState({ open: false, slot: null });
  const [activating, setActivating] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deployment/${deployment.id}`);
      setSlots(data.slots || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [deployment.id]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate this line? It will move to Deactivated designs — you can reactivate it later.')) return;
    try { await api.post(`/deployment/${deployment.id}/deactivate`); onRefresh(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleActivate = async () => {
    setActivating(true);
    try { await api.post(`/deployment/${deployment.id}/activate`); onRefresh(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
    setActivating(false);
  };

  const handleAddSlot = async (ldiId) => {
    try { await api.post(`/deployment/${deployment.id}/slots`, { line_design_item_id: ldiId }); fetchSlots(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleRemoveSlot = async (slotId) => {
    try { await api.delete(`/deployment/${deployment.id}/slots/${slotId}`); fetchSlots(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const handleDragStart = (e, slot) => {
    if (slot.is_vacant) return;
    setDragging({ slotId: slot.id });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(slot.id));
  };

  const handleDragOver = (e, slotId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(slotId);
  };

  const handleDragEnd = () => {
    setDragging(null);
    setDragOver(null);
  };

  const handleDrop = async (e, targetSlot) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging || dragging.slotId === targetSlot.id) { setDragging(null); return; }
    const fromSlot = slots.find(s => s.id === dragging.slotId);
    if (!fromSlot) { setDragging(null); return; }
    try {
      await api.post(`/deployment/${deployment.id}/slots/swap`, {
        slot_a: fromSlot.id,
        slot_b: targetSlot.id,
      });
      fetchSlots();
    } catch (e) { alert('Error swapping operators'); }
    setDragging(null);
  };

  const opGroups = {};
  slots.forEach(s => {
    const key = s.line_design_item_id;
    if (!opGroups[key]) opGroups[key] = {
      line_design_item_id: key,
      operation_name: s.operation_name,
      sam: s.sam,
      operators_count: s.operators_count,
      output_per_hr: s.output_per_hr,
      skill_req_pct: s.skill_req_pct,
      is_bottleneck: s.is_bottleneck,
      op_row: s.op_row,
      is_clubbed: false,
      clubbed_operation_name: null,
      slots: []
    };
    // A group can mix independent slots (is_clubbed=false) with one shared slot
    // (is_clubbed=true) — the op-level badge should show if ANY slot is shared.
    if (s.is_clubbed) {
      opGroups[key].is_clubbed = true;
      opGroups[key].clubbed_operation_name = s.clubbed_operation_name;
    }
    opGroups[key].slots.push(s);
  });

  const ops = Object.values(opGroups).sort((a, b) => a.op_row - b.op_row);
  // Recalculate bottleneck based on actual deployed skill% — override line design is_bottleneck
  const deployedBottleneckName = calcBottleneck(ops);
  ops.forEach(op => { op.is_deployed_bottleneck = op.operation_name === deployedBottleneckName; });
  // "Deployed" should reflect real headcount — a shared operator has 2 slot rows
  // (one per operation) but is 1 real person. Total is the LD's own planned
  // headcount (matches D9 in the reference sheet), not a raw slot-row count.
  const deployedCount = new Set(slots.filter(s => !s.is_vacant).map(s => s.employee_id)).size;
  const plannedTotal = ops.reduce((s, op) => s + (parseFloat(op.operators_count) || 0), 0);
  // A vacant clubbed pair is 2 slot rows but 1 missing real position — weight
  // clubbed rows at 0.5 so they don't get double-counted, consistent with how
  // shared slots are weighted everywhere else.
  const vacantCount = slots.filter(s => s.is_vacant).reduce((s, sl) => s + slotWeight(sl), 0);
  const efficiency = calcEfficiency(ops);
  const deployedBottleneck = calcBottleneck(ops);
  // E9 in the reference sheet: Line Design's OWN bottleneck output/hr (static,
  // from LD, not recalculated from deployment) — the denominator for each row's
  // "Available skill%".
  const ldBottleneckHr = ops.length ? Math.min(...ops.map(op => parseFloat(op.output_per_hr) || Infinity)) : 0;
  const effColor = efficiency === 0 ? 'var(--red)' : efficiency >= 75 ? 'var(--grn)' : efficiency >= 65 ? 'var(--amb)' : 'var(--red)';

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Btn size="sm" onClick={onBack}>← Back</Btn>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Line {deployment.line_number} · {deployment.style_number}</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 2 }}>{deployment.shift_name} · {fmtDate(deployment.deploy_date)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>Deployment efficiency</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: effColor }}>{efficiency.toFixed(1)}%</div>
          {efficiency === 0 && vacantCount > 0 && <div style={{ fontSize: 10, color: 'var(--red)' }}>{vacantCount} vacant — line stopped</div>}
        </div>
        <Badge type={deployment.status === 'Active' ? 'green' : 'amber'}>{deployment.status}</Badge>
        {deployment.status === 'Pending' && (
          <Btn variant="primary" onClick={handleActivate} disabled={activating}>{activating ? 'Activating…' : 'Activate'}</Btn>
        )}
        {deployment.status === 'Active' && (
          <Btn onClick={handleDeactivate} style={{ color: 'var(--amb)', borderColor: 'var(--amb)', background: 'var(--amb-bg)' }}>Deactivate</Btn>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <div className="kpi blue"><div className="kpi-label" style={{ color: 'var(--acc-t)' }}>Deployed</div><div className="kpi-val" style={{ color: 'var(--acc)' }}>{deployedCount}<span style={{ fontSize: 12, fontWeight: 400 }}> / {Math.round(plannedTotal)}</span></div></div>
        <div className="kpi red"><div className="kpi-label" style={{ color: 'var(--red)' }}>Vacant</div><div className="kpi-val" style={{ color: 'var(--red)' }}>{Math.round(vacantCount)}</div></div>
        <div className="kpi amber"><div className="kpi-label" style={{ color: 'var(--amb)' }}>Skill gaps</div><div className="kpi-val" style={{ color: 'var(--amb)' }}>{slots.filter(s => !s.is_vacant && parseFloat(s.skill_pct || 0) < parseFloat(s.skill_req_pct || 0)).length}</div></div>
        <div className="kpi"><div className="kpi-label">Plan efficiency</div><div className="kpi-val">{parseFloat(deployment.plan_efficiency || 0).toFixed(1)}%</div></div>
      </div>

      {loading ? <Loading /> : (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>⠿ Drag operator chips between slots to reassign — skill% recalculates for the new operation</div>
            <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      {['#', 'Operation', 'SAM', 'Req%', 'Operators', 'Deployed operators', 'Total', 'Available skill'].map((h, i) => (
                        <th key={i} style={{ padding: '7px 10px', borderBottom: '0.5px solid var(--border-md)', textAlign: 'left', fontSize: 10, fontWeight: 500, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ops.map((op, opIdx) => {
                      const rowTotal = opOutputHr(op);
                      const availableSkillPct = ldBottleneckHr > 0 && ldBottleneckHr !== Infinity
                        ? (rowTotal / ldBottleneckHr) * 100
                        : null;
                      const anyVacant = op.slots.some(s => s.is_vacant);
                      const allVacant = op.slots.every(s => s.is_vacant);
                      return (
                        <tr key={op.line_design_item_id} style={{ borderBottom: '0.5px solid var(--border)', background: op.is_deployed_bottleneck ? '#FFF5F5' : anyVacant ? '#FFF8F8' : '' }}>
                          <td style={{ padding: '8px 10px', color: 'var(--text-tertiary)', fontSize: 10.5 }}>{opIdx + 1}</td>
                          <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                            {op.is_deployed_bottleneck && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E24B4A', display: 'inline-block', marginRight: 5, verticalAlign: 'middle' }} />}
                            {op.operation_name}
                            {op.is_clubbed && (
                              op.clubbed_operation_name && op.clubbed_operation_name !== op.operation_name ? (
                                <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--acc-bg)', color: 'var(--acc)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>🔗 Clubbed with {op.clubbed_operation_name}</span>
                              ) : (
                                <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--acc-bg)', color: 'var(--acc)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>🔗 Shared across {op.slots.length} slots</span>
                              )
                            )}
                            {op.is_deployed_bottleneck && <span style={{ marginLeft: 6, fontSize: 9.5, background: 'var(--red-bg)', color: 'var(--red)', padding: '1px 6px', borderRadius: 10, fontWeight: 500 }}>Bottleneck</span>}
                          </td>
                          <td style={{ padding: '8px 10px' }}>{parseFloat(op.sam).toFixed(2)}</td>
                          <td style={{ padding: '8px 10px' }}><SkillBadge pct={op.skill_req_pct} /></td>
                          <td style={{ padding: '8px 10px' }}>
                            {op.is_clubbed ? (
                              <span style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>1 shared</span>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <button onClick={() => op.slots.length > 1 && handleRemoveSlot(op.slots[op.slots.length - 1].id)}
                                  style={{ width: 20, height: 20, borderRadius: '50%', border: '0.5px solid #F09595', background: 'var(--bg-primary)', cursor: 'pointer', color: 'var(--red)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                                <span style={{ fontSize: 13, fontWeight: 500, minWidth: 16, textAlign: 'center' }}>{op.slots.length}</span>
                                <button onClick={() => handleAddSlot(op.line_design_item_id)}
                                  style={{ width: 20, height: 20, borderRadius: '50%', border: '0.5px solid #97C459', background: 'var(--bg-primary)', cursor: 'pointer', color: 'var(--grn)', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', minWidth: 210 }}>
                            {op.slots.map(slot => {
                              const isDragOver = dragOver === slot.id;
                              const isDragging = dragging?.slotId === slot.id;
                              return (
                                <div
                                  key={slot.id}
                                  draggable={!slot.is_vacant && !slot.is_clubbed}
                                  onDragStart={e => handleDragStart(e, slot)}
                                  onDragOver={e => handleDragOver(e, slot.id)}
                                  onDragEnd={handleDragEnd}
                                  onDrop={e => handleDrop(e, slot)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    background: isDragOver ? 'var(--acc-bg)' : slot.is_vacant ? 'var(--red-bg)' : 'var(--bg-secondary)',
                                    border: `0.5px ${isDragOver ? 'solid var(--acc)' : slot.is_vacant ? 'dashed #F09595' : slot.match_tier === 'direct' ? 'solid #97C459' : slot.match_tier === 'operation' ? 'solid var(--acc)' : slot.match_tier === 'fund' ? 'solid #FAC775' : 'solid var(--border)'}`,
                                    borderRadius: 6, padding: '4px 7px', marginBottom: 3,
                                    fontSize: 11.5, cursor: slot.is_vacant ? 'default' : slot.is_clubbed ? 'default' : 'grab',
                                    opacity: isDragging ? 0.4 : 1,
                                    transition: 'opacity 0.1s, border-color 0.1s, background 0.1s',
                                  }}
                                >
                                  {!slot.is_vacant && !slot.is_clubbed && <span style={{ color: 'var(--text-tertiary)', fontSize: 12, flexShrink: 0 }}>⠿</span>}
                                  {!slot.is_vacant && slot.is_clubbed && <span title="Linked to clubbed operation — use ↔ to change" style={{ color: 'var(--text-tertiary)', fontSize: 12, flexShrink: 0 }}>🔗</span>}
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: slot.is_vacant ? 'var(--red)' : 'var(--text-primary)' }}>
                                    {slot.is_vacant ? 'Vacant' : slot.emp_name}
                                  </span>
                                  {!slot.is_vacant && slot.emp_shift_line_id && slot.emp_shift_line_id !== deployment.shift_line_id && (
                                    <span style={{ fontSize: 9, background: '#FFF3CD', color: '#856404', border: '0.5px solid #FFCA2C', borderRadius: 4, padding: '1px 5px', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                      L{slot.emp_line_number}
                                    </span>
                                  )}
                                  <SkillBadge pct={slot.is_vacant ? 0 : (slot.skill_pct ?? 0)} tier={slot.match_tier} />
                                  <button
                                    onClick={() => setAssignModal({ open: true, slot })}
                                    style={{ background: slot.is_vacant ? 'var(--acc)' : 'var(--bg-secondary)', color: slot.is_vacant ? '#fff' : 'var(--acc)', border: slot.is_vacant ? 'none' : '0.5px solid var(--acc)', borderRadius: 4, padding: '2px 6px', fontSize: 9.5, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                                  >{slot.is_vacant ? 'Assign' : '↔'}</button>
                                </div>
                              );
                            })}
                          </td>
                          <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                            {allVacant ? '—' : Math.round(rowTotal)}
                          </td>
                          <td style={{ padding: '8px 10px', fontSize: 11.5, color: availableSkillPct === null ? 'var(--text-tertiary)' : availableSkillPct < 100 ? 'var(--red)' : 'var(--grn)', fontWeight: 500 }}>
                            {allVacant || availableSkillPct === null ? '—' : `${availableSkillPct.toFixed(0)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div style={{ position: 'sticky', top: 16, alignSelf: 'flex-start' }}>
            <PoolPanel deploymentId={deployment.id} slots={slots} onReleaseElsewhere={fetchSlots} />
          </div>
        </div>
      )}

      <AssignModal
        open={assignModal.open}
        onClose={() => setAssignModal({ open: false, slot: null })}
        slot={assignModal.slot}
        deploymentId={deployment.id}
        onAssigned={() => { setAssignModal({ open: false, slot: null }); fetchSlots(); }}
      />
    </div>
  );
}

export default function Deployment() {
  const location = useLocation();
  const [view, setView] = useState('list');
  const [activeDeployment, setActiveDeployment] = useState(null);
  const [newModal, setNewModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    document.getElementById('lc-page-title').textContent = 'Deployment';
    if (location.state?.line_design_id) setNewModal(true);
  }, [location.state]);

  const handleOpen = (dep) => { setActiveDeployment(dep); setView('detail'); };
  const handleBack = () => { setView('list'); setActiveDeployment(null); setRefreshKey(k => k + 1); };
  const handleCreated = () => { setNewModal(false); setRefreshKey(k => k + 1); };

  if (view === 'detail' && activeDeployment) {
    return <DeploymentDetail deployment={activeDeployment} onBack={handleBack} onRefresh={handleBack} />;
  }

  return (
    <>
      <DeploymentLanding key={refreshKey} onOpen={handleOpen} onNew={() => setNewModal(true)} />
      <NewDeploymentModal open={newModal} onClose={() => setNewModal(false)} onCreated={handleCreated} />
    </>
  );
}